import * as Linking from 'expo-linking';
import { createLogger } from '@/lib/logger';
import { Platform } from 'react-native';
import Constants from 'expo-constants';

const log = createLogger('LINKING');

/**
 * Deep linking URLs for password reset and other flows
 *
 * IMPORTANT: All redirect URLs must be allow-listed in Supabase Dashboard
 * Navigate to: Authentication → URL Configuration → Redirect URLs
 *
 * Required URLs depend on your environment:
 * - Web: https://douevenlift.com/reset-password (and optionally https://douevenlift.com/*)
 * - Expo Go: The dynamic URL shown in console (changes per environment)
 * - Dev Build/Production: duel://reset-password (and optionally duel://*)
 *
 * Without these whitelisted, Supabase will reject password reset requests with redirect errors.
 */

// Development: Uses Expo's dynamically generated URL
// Production: Uses the custom app scheme
export const RESET_PASSWORD_URL = Linking.createURL('reset-password');

// Explicit production URL for Supabase configuration
export const APP_SCHEME_URL = 'duel://reset-password';

let branch: string;
let redirectTo: string;

if (Platform.OS === 'web') {
  branch = 'web';
  redirectTo = 'https://douevenlift.com/reset-password';
} else if (Constants.appOwnership === 'expo') {
  branch = 'expo-go';
  redirectTo = RESET_PASSWORD_URL;
} else {
  branch = 'dev-build/production';
  redirectTo = APP_SCHEME_URL;
}

if (__DEV__) {
  log.info('Auth reset config:', { branch, redirectTo });
}

/**
 * Parse deep link URL and extract query parameters
 */
export function parseDeepLink(url: string) {
  const { hostname, path, queryParams } = Linking.parse(url);
  return {
    hostname,
    path,
    queryParams,
  };
}

/**
 * Parse URL hash/fragment for Supabase recovery tokens
 * Format: #access_token=...&refresh_token=...&type=recovery
 */
export function parseUrlFragment(url: string): Record<string, string> {
  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) return {};

  const fragment = url.substring(hashIndex + 1);
  const params: Record<string, string> = {};

  fragment.split('&').forEach(pair => {
    const [key, value] = pair.split('=');
    if (key && value) {
      params[key] = decodeURIComponent(value);
    }
  });

  return params;
}

/**
 * Detect and parse Supabase password recovery URL
 * Supports both token formats:
 * - Format 1: #access_token=...&refresh_token=...&type=recovery
 * - Format 2: ?token_hash=...&type=recovery
 */
export function parseRecoveryUrl(url: string) {
  // Check for fragment-based tokens (Format 1)
  const fragmentParams = parseUrlFragment(url);
  if (fragmentParams.type === 'recovery' && fragmentParams.access_token && fragmentParams.refresh_token) {
    log.info('Recovery URL: session format');
    return {
      format: 'session' as const,
      accessToken: fragmentParams.access_token,
      refreshToken: fragmentParams.refresh_token,
    };
  }

  // Check for query-based token_hash (Format 2)
  const { queryParams } = Linking.parse(url);
  if (queryParams?.type === 'recovery' && queryParams?.token_hash) {
    log.info('Recovery URL: OTP format');
    return {
      format: 'otp' as const,
      tokenHash: queryParams.token_hash as string,
    };
  }

  return null;
}

/**
 * Create a deep link URL for a specific route
 */
export function createDeepLink(route: string, params?: Record<string, string>) {
  return Linking.createURL(route, { queryParams: params });
}

/**
 * Linking configuration for expo-router.
 * Handles both duel:// scheme and https://duel.app/ universal links.
 */
export const linkingConfig = {
  prefixes: [
    Linking.createURL('/'),   // duel:// scheme
    'https://duel.app',
    'https://douevenlift.com',
  ],
};
